import { SHEET_NAME as OTHER_AIR_EMISSIONS_SHEET } from "./detailsOfOtherAirEmissionsSheet";
import { SHEET_NAME as ENVIRONMENT_APPROVAL_SHEET } from "./environmentApprovalSheet";
import { SHEET_NAME as ENVIRONMENTAL_IMPACT_SHEET } from "./environmentalImpactSheet";
import { SHEET_NAME as WATER_STRESS_SHEET } from "./plantInAreaOfWaterStressSheet";
import { SHEET_NAME as INITIATIVE_SHEET } from "./initiativeToImproveResourceSheet";
import { SHEET_NAME as RENEWABLE_ENERGY_SHEET } from "./energyConsumedFromRenewableSheet";
import { SHEET_NAME as NR_ENERGY_SHEET } from "./energyConsumedFromNRSheet";
import {
  CALC_FILL,
  SUBHEADER_FILL,
  TABLE_HEADER_FILL,
  addExternalAgencyRows,
  addCategoryFyHeader,
  addInlineExternalAgencyRow,
  addParamFyHeader,
  addParamFyRow,
  addParamUnitFyHeader,
  addParamUnitFyRow,
  addQuestionRow,
  addSectionHeader,
  addShortAnswerRow,
  addTextAnswerRow,
  calcEnergyTotal,
  formatDischarge,
  formatUnit,
  getFyLabels,
  getReportFyLabels,
  internalSheetLink,
  mergeRow,
  readFY,
  safeText,
  setCell,
  setPrinciple5ColumnWidths,
  sumDischargeSources,
  sumFYCells,
  toYesNo,
  toYesNoNa,
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  COL_L,
  COL_M,
  COL_N,
  COL_END,
  COL_LABEL_END,
  COL_VALUE_START,
  COL_VALUE_END,
} from "./principle6SheetHelpers";

const PRINCIPLE_TITLE =
  "Businesses should respect and make efforts to protect and restore the environment.";

const RENEWABLE_KEYS = ["electricityConsumption", "fuelConsumption", "energyConsumption"];
const NON_RENEWABLE_KEYS = ["electricityConsumption", "fuelConsumption", "energyConsumption"];

const AIR_ROWS = [
  ["NOx", "nox"],
  ["SOx", "sox"],
  ["Particulate matter (PM)", "pm"],
  ["Persistent organic pollutants (POP)", "pop"],
  ["Volatile organic compounds (VOC)", "voc"],
  ["Hazardous air pollutants (HAP)", "hap"],
];

const GHG_ROWS = [
  [
    "Total Scope 1 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
    "scope1",
  ],
  [
    "Total Scope 2 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
    "scope2",
  ],
  [
    "Total Scope 1 and Scope 2 emission intensity per rupee of turnover (Total Scope 1 and Scope 2 GHG emissions / Revenue from operations)",
    "totalOfScope1and2Intensity",
  ],
  [
    "Total Scope 1 and Scope 2 emission intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total Scope 1 and Scope 2 GHG emissions / Revenue from operations adjusted for PPP)",
    "scope1and2emissionPPP",
  ],
  [
    "Total Scope 1 and Scope 2 emission intensity in terms of physical output",
    "scope1and2emissionIntensityPhysical",
  ],
  [
    "Total Scope 1 and Scope 2 emission intensity (optional) – the relevant metric may be selected by the entity",
    "scope1and2EmissionIntensity",
  ],
];

const WATER_ROWS = [
  ["(i) Surface water", "surfaceWater"],
  ["(ii) Groundwater", "groundWater"],
  ["(iii) Third party water", "thirdPartyWater"],
  ["(iv) Seawater / desalinated water", "seaWater"],
  ["(v) Others", "others"],
  ["Total volume of water withdrawal (i + ii + iii + iv + v)", "totalVolumnWithdrawal", true],
  ["Total volume of water consumption", "totalWaterConsumption"],
  [
    "Water intensity per rupee of turnover (Total water consumption / Revenue from operations)",
    "waterIntensityTurnover",
  ],
  [
    "Water intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total water consumption / Revenue from operations adjusted for PPP)",
    "waterIntensityPuchasingPower",
  ],
  ["Water intensity in terms of physical output", "waterIntensityPhyiscalOutput"],
  [
    "Water intensity (optional) – the relevant metric may be selected by the entity",
    "waterIntensityOptional",
  ],
];

const WASTE_GEN_ROWS = [
  ["Plastic waste (A)", "plastics"],
  ["E-waste (B)", "eWaste"],
  ["Bio-medical waste (C)", "bioMediacal"],
  ["Construction and demolition waste (D)", "construtionAndDemolition"],
  ["Battery waste (E)", "batteryWaste"],
  ["Radioactive waste (F)", "radioactiveWaste"],
  ["Other Hazardous waste (G)", "otherHazardous"],
  ["Other Non-hazardous waste generated (H)", "otherNonHazardous"],
];

const WASTE_REC_ROWS = [
  ["(i) Recycled", "recycled"],
  ["(ii) Re-used", "reUsed"],
  ["(iii) Other recovery operations", "other"],
];

const WASTE_DISP_ROWS = [
  ["(i) Incineration", "inceneration"],
  ["(ii) Landfilling", "lanfilling"],
  ["(iii) Other disposal operations", "other"],
];

const DISCHARGE_SOURCES = [
  ["(i) To Surface water", "surfaceWater"],
  ["(ii) To Groundwater", "groundWater"],
  ["(iii) To Seawater", "producedWater"],
  ["(iv) Sent to third-parties", "thirdPartyWater"],
  ["(v) Others", "other"],
];

const SCOPE3_ROWS = [
  [
    "Total Scope 3 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
    "totalScope3Emission",
  ],
  ["Total Scope 3 emissions per rupee turnover", "totalScope3EmissionPerRupeeturnOver"],
  [
    "Total Scope 3 emission intensity (optional) - the relevant metric may be selected by the entity",
    "emissionIntensity",
  ],
];

function formatNonComplianceDetails(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return "";

  return list
    .map((item, idx) => {
      const parts = [
        safeText(item?.specifyLaw),
        safeText(item?.details),
        safeText(item?.penalties),
        safeText(item?.correctiveAction),
      ].filter((part) => part && part !== "-");
      return parts.length ? `${idx + 1}. ${parts.join("; ")}` : "";
    })
    .filter(Boolean)
    .join("\n");
}

function addEnergySectionRow(ws, row, label, sheetName) {
  return addQuestionRow(ws, row, label, "Show Detail", {
    link: internalSheetLink(sheetName),
    valueAlign: "center",
    valueVertical: "middle",
    bold: true,
  });
}

function addEnergyBlock(ws, row, energy, fyCurrent, fyPrevious) {
  const unit =
    formatUnit(
      energy?.fromRenewableSource?.electricityConsumption?.currentUnit ||
        energy?.fromRenewableSource?.electricityConsumption?.previousUnit
    ) || "Gigajoule (GJ)";

  row = addQuestionRow(
    ws,
    row,
    "1. Details of total energy consumption (in Joules or multiples) and energy intensity, in the following format:",
    "",
    { fullWidth: true }
  );

  row = addEnergySectionRow(ws, row, "From renewable sources", RENEWABLE_ENERGY_SHEET);
  row = addParamUnitFyHeader(ws, row, fyCurrent, fyPrevious);

  const renewable = energy?.fromRenewableSource || {};
  const renLabels = [
    ["Total electricity consumption (A)", "electricityConsumption"],
    ["Total fuel consumption (B)", "fuelConsumption"],
    ["Energy consumption through other sources (C)", "energyConsumption"],
  ];
  renLabels.forEach(([label, key]) => {
    row = addParamUnitFyRow(
      ws,
      row,
      label,
      unit,
      readFY(renewable[key], "current"),
      readFY(renewable[key], "previous")
    );
  });

  const renTotal = calcEnergyTotal(renewable, RENEWABLE_KEYS);
  row = addParamUnitFyRow(
    ws,
    row,
    "Total energy consumed from renewable sources (A+B+C)",
    unit,
    renTotal.current,
    renTotal.previous,
    { fill: CALC_FILL, bold: true }
  );

  row = addEnergySectionRow(ws, row, "From non-renewable sources", NR_ENERGY_SHEET);
  row = addParamUnitFyHeader(ws, row, fyCurrent, fyPrevious);

  const nonRenewable = energy?.fromNonRenewableSource || {};
  const nonLabels = [
    ["Total electricity consumption (D)", "electricityConsumption"],
    ["Total fuel consumption (E)", "fuelConsumption"],
    ["Energy consumption through other sources (F)", "energyConsumption"],
  ];
  nonLabels.forEach(([label, key]) => {
    row = addParamUnitFyRow(
      ws,
      row,
      label,
      unit,
      readFY(nonRenewable[key], "current"),
      readFY(nonRenewable[key], "previous")
    );
  });

  const nonTotal = calcEnergyTotal(nonRenewable, NON_RENEWABLE_KEYS);
  row = addParamUnitFyRow(
    ws,
    row,
    "Total energy consumed from non-renewable sources (D+E+F)",
    unit,
    nonTotal.current,
    nonTotal.previous,
    { fill: CALC_FILL, bold: true }
  );

  const grandCurr =
    (renTotal.current ?? 0) + (nonTotal.current ?? 0) || readFY(energy?.energyIntensity?.energyIntensity, "current");
  const grandPrev =
    (renTotal.previous ?? 0) + (nonTotal.previous ?? 0) ||
    readFY(energy?.energyIntensity?.energyIntensity, "previous");

  const intensity = energy?.energyIntensity || {};
  const intensityRows = [
    ["Total energy consumed (A+B+C+D+E+F)", "energyIntensity", unit],
    [
      "Energy intensity per rupee of turnover (Total energy consumed / Revenue from operations)",
      "energyIntensityTurnover",
      unit,
    ],
    [
      "Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total energy consumed / Revenue from operations adjusted for PPP)",
      "energyIntensityPuchasingPower",
      unit,
    ],
    ["Energy Intensity in terms of physical Output", "energyPhysicalOutput", unit],
    [
      "Energy intensity (optional) – the relevant metric may be selected by the entity",
      "energyIntensityOptional",
      unit,
    ],
  ];

  intensityRows.forEach(([label, key, rowUnit], idx) => {
    const curr = idx === 0 ? grandCurr : readFY(intensity[key], "current");
    const prev = idx === 0 ? grandPrev : readFY(intensity[key], "previous");
    row = addParamUnitFyRow(ws, row, label, rowUnit, curr, prev, {
      fill: idx === 0 ? CALC_FILL : undefined,
    });
  });

  row = addExternalAgencyRows(ws, row, energy?.energyIndependentAssessment);
  return row;
}

function addWaterDischargeBlock(ws, row, essential, fyCurrent, fyPrevious) {
  ["G", "H", "I", "J"].forEach((col) => {
    ws.getColumn(col).width = 14;
  });

  row = addQuestionRow(
    ws,
    row,
    "4. Provide the following details related to water discharged:",
    "",
    { fullWidth: true }
  );
  row = addQuestionRow(
    ws,
    row,
    "Water discharge by destination and level of treatment (in kilolitres)",
    "",
    { fullWidth: true, bold: true }
  );

  mergeRow(ws, row, COL_D, COL_E, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_G, COL_H, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_I, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  mergeRow(ws, row, COL_D, COL_E, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_F, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_G, "Water Discharge (in KL)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Specify level of treatment", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "Water Discharged (in KL)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "Specify level of treatment", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  DISCHARGE_SOURCES.forEach(([label, key]) => {
    const c = essential?.waterDischargeCurrent?.[key] || {};
    const p = essential?.waterDischargePrevious?.[key] || {};

    mergeRow(ws, row, COL_D, COL_E, label, { align: "left" });
    setCell(ws, row, COL_F, "Not treated", { align: "left" });
    setCell(ws, row, COL_G, formatDischarge(c.dischargeWithoutTreatment), { align: "center" });
    setCell(ws, row, COL_H, "-", { align: "center" });
    setCell(ws, row, COL_I, formatDischarge(p.dischargeWithoutTreatment), { align: "center" });
    setCell(ws, row, COL_J, "-", { align: "center" });
    row += 1;

    mergeRow(ws, row, COL_D, COL_E, "-", { align: "left" });
    setCell(ws, row, COL_F, "With treatment", { align: "left" });
    setCell(ws, row, COL_G, formatDischarge(c.dischargeAfterTreatment), { align: "center" });
    setCell(ws, row, COL_H, safeText(c.treatmentLevel ?? c.treatMentLeval, ""), { align: "center" });
    setCell(ws, row, COL_I, formatDischarge(p.dischargeAfterTreatment), { align: "center" });
    setCell(ws, row, COL_J, safeText(p.treatmentLevel ?? p.treatMentLeval, ""), { align: "center" });
    row += 1;
  });

  const dischargeKeys = ["surfaceWater", "groundWater", "producedWater", "thirdPartyWater", "other"];
  const totalCurr = sumDischargeSources(essential?.waterDischargeCurrent, dischargeKeys);
  const totalPrev = sumDischargeSources(essential?.waterDischargePrevious, dischargeKeys);

  mergeRow(ws, row, COL_D, COL_F, "Total water discharged (in kilolitres)", {
    align: "left",
    fill: CALC_FILL,
    bold: true,
  });
  mergeRow(ws, row, COL_G, COL_H, formatDischarge(totalCurr ?? essential?.waterDischargeCurrent?.total), {
    align: "center",
    fill: CALC_FILL,
  });
  mergeRow(ws, row, COL_I, COL_J, formatDischarge(totalPrev ?? essential?.waterDischargePrevious?.total), {
    align: "center",
    fill: CALC_FILL,
  });
  row += 1;

  row = addExternalAgencyRows(ws, row, essential?.waterDischargeExternalAgency);
  return row;
}

function addWasteBlock(ws, row, essential, fyCurrent, fyPrevious) {
  const wasteMgmt = essential?.wasteManagementDetails || essential;
  const wg = wasteMgmt?.wasteGenerated || essential?.wasteGenerated || {};
  const wi = wasteMgmt?.wasteIntensity || essential?.wasteIntensity || {};
  const wr = wasteMgmt?.wasteGeneratedCategory || essential?.wasteGeneratedCategory || {};
  const wd = wasteMgmt?.wasteDesposed || essential?.wasteDesposed || {};

  row = addQuestionRow(
    ws,
    row,
    "9. Provide details related to waste management by the entity, in the following format:",
    "",
    { fullWidth: true }
  );
  row = addQuestionRow(ws, row, "Total Waste generated (in metric tonnes)", "", { fullWidth: true, bold: true });
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);

  WASTE_GEN_ROWS.forEach(([label, key]) => {
    row = addParamFyRow(ws, row, label, readFY(wg[key], "current"), readFY(wg[key], "previous"));
  });

  const genKeys = WASTE_GEN_ROWS.map(([, key]) => key);
  row = addParamFyRow(
    ws,
    row,
    "Total (A + B + C + D + E + F + G + H)",
    sumFYCells(wg, genKeys, "current"),
    sumFYCells(wg, genKeys, "previous"),
    { fill: CALC_FILL, bold: true }
  );

  const intensityRows = [
    [
      "Waste intensity per rupee of turnover (Total waste generated / Revenue from operations)",
      "wasteIntensity",
    ],
    [
      "Waste Intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total waste generated / Revenue from operations adjusted for PPP)",
      "wasteIntensityPPP",
    ],
    ["Waste intensity in terms of physical output", "wasteIntensityPhysical"],
    [
      "Waste intensity (optional) – the relevant metric may be selected by the entity",
      "wasteIntensityOptiomal",
    ],
  ];
  intensityRows.forEach(([label, key]) => {
    row = addParamFyRow(ws, row, label, readFY(wi[key], "current"), readFY(wi[key], "previous"));
  });

  row = addQuestionRow(
    ws,
    row,
    "For each category of waste generated, total waste recovered through recycling, re-using or other recovery operations (in metric tonnes)",
    "",
    { fullWidth: true, bold: true }
  );
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);
  WASTE_REC_ROWS.forEach(([label, key]) => {
    row = addParamFyRow(ws, row, label, readFY(wr[key], "current"), readFY(wr[key], "previous"));
  });
  const recKeys = WASTE_REC_ROWS.map(([, key]) => key);
  row = addParamFyRow(
    ws,
    row,
    "Total",
    sumFYCells(wr, recKeys, "current"),
    sumFYCells(wr, recKeys, "previous"),
    { fill: CALC_FILL, bold: true }
  );

  row = addQuestionRow(
    ws,
    row,
    "For each category of waste generated, total waste disposed by nature of disposal method (in metric tonnes)",
    "",
    { fullWidth: true, bold: true }
  );
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);
  WASTE_DISP_ROWS.forEach(([label, key]) => {
    row = addParamFyRow(ws, row, label, readFY(wd[key], "current"), readFY(wd[key], "previous"));
  });
  const dispKeys = WASTE_DISP_ROWS.map(([, key]) => key);
  row = addParamFyRow(
    ws,
    row,
    "Total",
    sumFYCells(wd, dispKeys, "current"),
    sumFYCells(wd, dispKeys, "previous"),
    { fill: CALC_FILL, bold: true }
  );

  const wasteAssessment = wasteMgmt?.independentAssessment ?? essential?.independentAssessment;
  row = addExternalAgencyRows(ws, row, wasteAssessment);
  return row;
}

export function buildPrinciple6Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 6", { views: [{ showGridLines: true }] });
  setPrinciple5ColumnWidths(ws);

  const principle = sectionC?.principle6 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const energy = essential?.energyConsumptionIntensity || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);
  const { current: fyCurrentReport, previous: fyPreviousReport } = getReportFyLabels(reportingYear);

  let row = 3;
  row = addSectionHeader(ws, row, `PRINCIPLE 6: ${PRINCIPLE_TITLE}`);
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addEnergyBlock(ws, row, energy, fyCurrent, fyPrevious);

  row = addShortAnswerRow(
    ws,
    row,
    "2. Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India?",
    toYesNo(essential?.patSchemeDetails?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "If yes, disclose whether targets set under the PAT scheme have been achieved. In case targets have not been achieved, provide the remedial action taken, if any.",
    essential?.patSchemeDetails?.action ? essential?.patSchemeDetails?.remark : ""
  );

  row = addQuestionRow(
    ws,
    row,
    "3. Provide details of the following disclosures related to water, in the following format:",
    "",
    { fullWidth: true }
  );
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);
  const wd = essential?.waterDisclosuresRelated || {};
  const withdrawalKeys = ["surfaceWater", "groundWater", "thirdPartyWater", "seaWater", "others"];
  WATER_ROWS.forEach(([label, key, isTotal]) => {
    let curr = readFY(wd[key], "current");
    let prev = readFY(wd[key], "previous");
    if (isTotal) {
      curr = sumFYCells(wd, withdrawalKeys, "current") ?? curr;
      prev = sumFYCells(wd, withdrawalKeys, "previous") ?? prev;
    }
    row = addParamFyRow(ws, row, label, curr, prev, isTotal ? { fill: CALC_FILL, bold: true } : undefined);
  });
  row = addExternalAgencyRows(ws, row, wd?.externalAgency);

  row = addWaterDischargeBlock(ws, row, essential, fyCurrent, fyPrevious);

  row = addShortAnswerRow(
    ws,
    row,
    "5. Has the entity implemented a mechanism for Zero Liquid Discharge?",
    toYesNo(essential?.zeroLiquidDischarge?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "If yes, provide details of its coverage and implementation.",
    essential?.zeroLiquidDischarge?.action ? essential?.zeroLiquidDischarge?.remark : ""
  );

  row = addQuestionRow(
    ws,
    row,
    "6. Please provide details of air emissions (other than GHG emissions) by the entity, in the following format:",
    "",
    { fullWidth: true }
  );
  row = addParamUnitFyHeader(ws, row, fyCurrent, fyPrevious, "Please specify unit");
  const airUnit = formatUnit(essential?.airEmissons?.nox?.currentUnit, "ug/m3");
  AIR_ROWS.forEach(([label, key]) => {
    row = addParamUnitFyRow(
      ws,
      row,
      label,
      airUnit,
      readFY(essential?.airEmissons?.[key], "current"),
      readFY(essential?.airEmissons?.[key], "previous")
    );
  });
  mergeRow(ws, row, COL_D, COL_LABEL_END, "Others – please specify", { align: "left" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, "Show Detail", {
    align: "center",
    link: `#'${OTHER_AIR_EMISSIONS_SHEET}'!D3`,
  });
  row += 1;
  row = addExternalAgencyRows(ws, row, essential?.airEmissionAssessment);

  row = addQuestionRow(
    ws,
    row,
    "7. Provide details of greenhouse gas emissions (Scope 1 and Scope 2 emissions) & its intensity, in the following format:",
    "",
    { fullWidth: true }
  );
  row = addParamFyHeader(ws, row, fyCurrent, fyPrevious);
  const ghg = essential?.greenHouseGasEmissions || {};
  GHG_ROWS.forEach(([label, key]) => {
    row = addParamFyRow(
      ws,
      row,
      label,
      readFY(ghg[key], "current"),
      readFY(ghg[key], "previous")
    );
  });
  row = addExternalAgencyRows(ws, row, ghg?.externalAgency);

  row = addShortAnswerRow(
    ws,
    row,
    "8. Does the entity have any project related to reducing Green House Gas emission?",
    toYesNo(essential?.greenHouseGasBooleanEmissions?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "If Yes, then provide details.",
    essential?.greenHouseGasBooleanEmissions?.action
      ? essential?.greenHouseGasBooleanEmissions?.remark
      : ""
  );

  row = addWasteBlock(ws, row, essential, fyCurrent, fyPrevious);

  row = addTextAnswerRow(
    ws,
    row,
    "10. Briefly describe the waste management practices adopted in your establishments. Describe the strategy adopted by your company to reduce usage of hazardous and toxic chemicals in your products and processes and the practices adopted to manage such wastes.",
    essential?.wasterManagmentPractices
  );

  row = addQuestionRow(
    ws,
    row,
    "11. If the entity has operations/offices in/around ecologically sensitive areas (such as national parks, wildlife sanctuaries, biosphere reserves, wetlands, biodiversity hotspots, forests, coastal regulation zones etc.) where environmental approvals / clearances are required, please specify details in the following format:",
    "Show Detail",
    {
      link: internalSheetLink(ENVIRONMENT_APPROVAL_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "12. Details of environmental impact assessments of projects undertaken by the entity based on applicable laws, in the current financial year:",
    "Show Detail",
    {
      link: internalSheetLink(ENVIRONMENTAL_IMPACT_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addShortAnswerRow(
    ws,
    row,
    "13. Is the entity compliant with the applicable environmental law/ regulations/ guidelines in India; such as the Water (Prevention and Control of Pollution) Act, Air (Prevention and Control of Pollution) Act, Environment protection act and rules thereunder (Y/N/NA).",
    toYesNoNa(essential?.entityComplain)
  );
  if (String(essential?.entityComplain) === "false") {
    row = addTextAnswerRow(
      ws,
      row,
      "If not, provide details of all such non-compliances, in the following format:",
      formatNonComplianceDetails(essential?.entityComplainwithEnvironmentLaw)
    );
  } else {
    row = addTextAnswerRow(
      ws,
      row,
      "If not, provide details of all such non-compliances, in the following format:",
      ""
    );
  }

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);

  row = addQuestionRow(
    ws,
    row,
    "1. Water withdrawal, consumption and discharge in areas of water stress (in kilolitres):",
    "",
    { fullWidth: true }
  );
  row = addQuestionRow(
    ws,
    row,
    "For each facility / plant located in areas of water stress, provide the following information:",
    "Show Detail",
    {
      link: internalSheetLink(WATER_STRESS_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );
  row = addExternalAgencyRows(ws, row, leadership?.waterIndependantAssessment);

  row = addQuestionRow(
    ws,
    row,
    "2. Please provide details of total Scope 3 emissions & its intensity, in the following format:",
    "",
    { fullWidth: true }
  );
  row = addCategoryFyHeader(ws, row, fyCurrentReport, fyPreviousReport);
  const scope3 = leadership?.scope3EmissionIntensity || {};
  SCOPE3_ROWS.forEach(([label, key]) => {
    row = addParamFyRow(
      ws,
      row,
      label,
      readFY(scope3[key], "current"),
      readFY(scope3[key], "previous")
    );
  });
  row = addInlineExternalAgencyRow(
    ws,
    row,
    leadership?.externalAgency ?? scope3?.externalAgency
  );

  row = addTextAnswerRow(
    ws,
    row,
    "3. With respect to the ecologically sensitive areas reported at Question 11 of Essential Indicators above, provide details of significant direct & indirect impact of the entity on biodiversity in such areas along-with prevention and remediation activities.",
    leadership?.bioDiversityPrevention
  );

  row = addQuestionRow(
    ws,
    row,
    "4. If the entity has undertaken any specific initiatives or used innovative technology or solutions to improve resource efficiency, or reduce impact due to emissions / effluent discharge / waste generated, please provide details of the same as well as outcome of such initiatives, as per the following format:",
    "Show Detail",
    {
      link: internalSheetLink(INITIATIVE_SHEET),
      valueAlign: "center",
      valueVertical: "middle",
    }
  );

  row = addShortAnswerRow(
    ws,
    row,
    "5. Does the entity have a business continuity and disaster management plan?",
    toYesNo(leadership?.busineessDisasterPlan?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "Details of entity at which business continuity and disaster management plan is placed or weblink.",
    leadership?.busineessDisasterPlan?.action
      ? [leadership?.busineessDisasterPlan?.remark, leadership?.busineessDisasterPlan?.webLink]
          .filter(Boolean)
          .join(" | ")
      : ""
  );

  row = addTextAnswerRow(
    ws,
    row,
    "6. Disclose any significant adverse impact to the environment, arising from the value chain of the entity. What mitigation or adaptation measures have been taken by the entity in this regard.",
    leadership?.disCloseAdverseImpactEnvironment
  );

  row = addShortAnswerRow(
    ws,
    row,
    "7. Percentage of value chain partners (by value of business done with such partners) that were assessed for environmental impacts.",
    safeText(leadership?.valueChainPartnerAssessment)
  );

  row = addQuestionRow(
    ws,
    row,
    "8. How many Green Credits have been generated or procured:",
    "",
    { fullWidth: true }
  );
  row = addTextAnswerRow(
    ws,
    row,
    "a. By the listed entity:",
    [leadership?.greenCreditsByEntityCount, leadership?.greenCreditsByEntityRemark]
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
      .join(" - ") || "-"
  );
  row = addTextAnswerRow(
    ws,
    row,
    "b. By the top ten (in terms of value of purchases and sales, respectively) value chain partners:",
    [leadership?.greenCreditsByValueChainPartnerCount, leadership?.greenCreditsByValueChainPartnerRemark]
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
      .join(" - ") || "-"
  );

  return ws;
}
